import { Box, Button, Menu, Text, Tooltip } from "@mantine/core";
import { useEffect, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  LuCheck,
  LuEye,
  LuFileText,
  LuShieldCheck,
  LuTrash2,
  LuWrench,
} from "react-icons/lu";
import { useSelector } from "react-redux";
import {
  getNoteTypes,
  noteTypeApproval,
  remoteNoteType,
} from "../../../services/note";
import useStyles from "./style";
import deleteModalIcon from "../../../images/deleteModalIcon.png";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ApprovalModal from "./ApprovalModal";

// System (general) note types each get their own distinct icon color; custom
// note types are always gray. Known system types use the mockup palette; any
// other system type falls back to a stable hashed color so it's still unique.
const TYPE_TINT = {
  "Progress Note": ["#EFF4FF", "#2E90FA"],
  "Group Note": ["#F4F3FF", "#5925DC"],
  "Essential Support Note": ["#ECFDF3", "#12B76A"],
  "Incident Report": ["#FEF3F2", "#F04438"],
  "Billing Services Note": ["#FFFAEB", "#F79009"],
  "Intervention Progress Note": ["#F0F9FF", "#0BA5EC"],
};
const SYSTEM_PALETTE = [
  ["#EFF4FF", "#2E90FA"],
  ["#F4F3FF", "#5925DC"],
  ["#ECFDF3", "#12B76A"],
  ["#FEF3F2", "#F04438"],
  ["#FFFAEB", "#F79009"],
  ["#F0F9FF", "#0BA5EC"],
  ["#FDF2FA", "#C11574"],
];
const GRAY_TINT = ["var(--app-surface-2)", "var(--app-text-muted)"];

// Built-in descriptions for the system note types (mirrors the Studio mockup),
// used when the note has no description of its own.
const TYPE_DESC = {
  "Progress Note":
    "Daily progress documentation with goals, interventions and outcomes.",
  "Group Note": "Shared session note supporting multiple clients at once.",
  "Essential Support Note":
    "Quick support documentation for essential daily activities.",
  "Incident Report":
    "Structured incident capture with severity, notifications and follow-up.",
  "Billing Services Note":
    "Service delivery note tied to billing units and service dates.",
  "Intervention Progress Note":
    "Progress documentation tied to intervention goals, behavior and response.",
};

// The built-in system note types render a fixed multi-section layout that is
// hardcoded in the create drawer (not stored as `sections[]`), so their cards
// would otherwise fall back to "1 section". This mirrors those drawer sections
// so the card reflects the real structure (e.g. Incident Report → 8 sections).
const TYPE_SECTIONS = {
  "Incident Report": [
    "Incident Classification",
    "When & Where",
    "People Involved",
    "What Happened",
    "Injuries & Medical Attention",
    "Restraint / Physical Intervention",
    "Notifications Made",
    "Follow-up & Preventive Action",
  ],
  "Progress Note": ["Client & Staff", "Support Plan Outcomes", "Note"],
  "Intervention Progress Note": [
    "Client & Staff",
    "Intervention Plan Outcomes",
    "Note",
  ],
  "Essential Support Note": ["Client & Staff", "Essential Support", "Note"],
  "Group Note": ["Client & Staff", "Note"],
  "Billing Services Note": ["Client & Staff", "Note"],
};

const hashIndex = (str, mod) => {
  let h = 0;
  for (let i = 0; i < String(str).length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % mod;
};

export const NoteCard = ({ note, onOpenBuilder }) => {
  const { profile } = useSelector((state) => state?.profile);
  const { classes, cx } = useStyles({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading] = useState(false);
  const [approvalRequired, setActive] = useState(note?.approvalRequired || false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  // A "general" note type is a built-in/system template: it can be viewed and
  // have its approval toggled, but never edited, rebuilt, or deleted. Only
  // custom (general === false) note types are fully editable.
  const isSystem = !!note?.general;
  const isIncidentReport = note?.name === "Incident Report";

  const fieldCount = note?.custom?.length || 0;
  const sectionCount =
    Array.isArray(note?.sections) && note.sections.length
      ? note.sections.length
      : TYPE_SECTIONS[note?.name]?.length || (fieldCount ? 1 : 0);

  const [tintBg, tintFg] = isSystem
    ? TYPE_TINT[note?.name] ||
      SYSTEM_PALETTE[hashIndex(note?.name || note?.id || "", SYSTEM_PALETTE.length)]
    : GRAY_TINT;

  useEffect(() => {
    if (note?.general === false) {
      if (note?.approvalRequired !== undefined) setActive(note?.approvalRequired);
    } else {
      const adminIndex = note?.adminApprovals?.findIndex(
        (_v) => _v?.admin === profile?.user?.id
      );
      if (adminIndex !== undefined && adminIndex !== -1) {
        setActive(note?.adminApprovals[adminIndex]?.status);
      } else if (note?.approvalRequired !== undefined) {
        setActive(note?.approvalRequired);
      }
    }
  }, [note]);

  const updateStatus = async (isChecked) => {
    try {
      const response = await noteTypeApproval({
        id: note?.id,
        approvalRequired: isChecked,
      });
      if (!successModalOpen && response.status === 200) {
        setSuccessModalOpen(true);
      } else {
        setActive(!isChecked);
      }
    } catch (error) {
      console.error("Error updating note status:", error);
    }
  };

  const handleDelete = () => {
    remoteNoteType(note?.id);
    setDeleteModalOpen(false);
  };

  return (
    <>
      <Box className={classes.tcard}>
        {/* header: icon + name + fields/sections meta + actions menu */}
        <Box className={classes.tcHead}>
          <Box className={classes.tcIcon} style={{ background: tintBg, color: tintFg }}>
            <LuFileText size={18} />
          </Box>
          <Box style={{ minWidth: 0 }}>
            <Text className={classes.tcName}>{note?.name}</Text>
            <Text className={classes.tcMeta}>
              {fieldCount} field{fieldCount === 1 ? "" : "s"} · {sectionCount}{" "}
              section{sectionCount === 1 ? "" : "s"}
            </Text>
          </Box>

          <Menu shadow="md" width={170} position="bottom-end" withinPortal>
            <Menu.Target>
              <Button variant="unstyled" className={classes.tcKebab}>
                <BsThreeDotsVertical
                  size={18}
                  color="var(--app-text-muted)"
                  style={{ cursor: "pointer" }}
                />
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {/* System note types open the same Studio as custom ones, but
                  read-only — sections/fields render exactly as stored,
                  nothing can be added, removed, or edited. Only custom
                  (general === false) note types are fully editable there. */}
              {isSystem && onOpenBuilder && (
                <Menu.Item icon={<LuEye size={15} />} onClick={() => onOpenBuilder(note)}>
                  View
                </Menu.Item>
              )}
              {/* Approval requirement can be toggled on any note type except
                  Incident Reports (which always require supervisor sign-off). */}
              {!isIncidentReport && (
                <Menu.Item
                  icon={<LuShieldCheck size={15} />}
                  rightSection={
                    approvalRequired ? (
                      <LuCheck size={14} color="var(--app-primary)" />
                    ) : null
                  }
                  closeMenuOnClick={false}
                  disabled={loading}
                  onClick={() => updateStatus(!approvalRequired)}
                >
                  Approval required
                </Menu.Item>
              )}
              {!isSystem && <Menu.Divider />}
              {/* Build / delete are only available on custom note types. */}
              {!isSystem && onOpenBuilder && (
                <Menu.Item
                  icon={<LuWrench size={15} />}
                  onClick={() => onOpenBuilder(note)}
                >
                  Open builder
                </Menu.Item>
              )}
              {!isSystem && (
                <Menu.Item
                  color="red"
                  icon={<LuTrash2 size={15} />}
                  onClick={() => setDeleteModalOpen(true)}
                >
                  Delete
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Box>

        {/* description */}
        <Text className={classes.tcDesc}>
          {note?.description ||
            TYPE_DESC[note?.name] ||
            (isSystem ? "" : "No description added yet.")}
        </Text>

        {/* footer: badges + primary action */}
        <Box className={classes.tcFoot}>
          {isSystem ? (
            <Tooltip
              label="System templates can't be edited, rebuilt or deleted"
              withArrow
              multiline
              width={200}
            >
              <Text className={cx(classes.tcBadge, classes.tcBadgeSystem)}>
                System
              </Text>
            </Tooltip>
          ) : (
            <Text className={cx(classes.tcBadge, classes.tcBadgeCustom)}>
              Custom
            </Text>
          )}
          {approvalRequired && (
            <Text className={cx(classes.tcBadge, classes.tcBadgeApproval)}>
              Approval
            </Text>
          )}

          <Box className={classes.tcFootSpacer} />

          <button
            type="button"
            className={classes.tcBtn}
            onClick={() => onOpenBuilder?.(note)}
          >
            {isSystem ? "View" : "Open builder"}
          </button>
        </Box>
      </Box>

      <ApprovalModal
        opened={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          getNoteTypes(1);
        }}
        approvalRequired={approvalRequired}
      />
      <CustomModal
        modalState={deleteModalOpen}
        modalTopImg={deleteModalIcon}
        modalTitle={"Delete Note"}
        modalText={
          "Are you sure you want to delete this note? This action cannot be undone."
        }
        closeModal={setDeleteModalOpen}
        buttonTwoFunc={handleDelete}
        colorOne={"gray"}
        colorTwo={"red"}
        btnTextOne="Cancel"
        btnTextTwo="Delete"
      />
    </>
  );
};
